<template src="./keyword-dedup-list.component.html"></template>
<style scoped src="./keyword-dedup-list.component.css"></style>
<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import Sidebar from '@/components/sidebar/sidebar.component.vue';
import api from '@/api';
import {getErrorMessage, noop, Pagination} from '@/helpers';
import {toasted} from '@/plugins/toasted';
import type {KeywordDedupGroup, KeywordDedupListResponse, KeywordDedupListRow} from '@/models';

function getQueryParamValue(query: Record<string, any>, key: string) {
  const value = query[key];
  if (Array.isArray(value)) return String(value[0] ?? '');
  return typeof value === 'string' ? value : '';
}

function normalizeMarket(value: string) {
  return value.trim().replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
}

function isValidMarket(value: string) {
  return /^[A-Z]{2}$/.test(value);
}

function normalizeKeywordMatchValue(value: string) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export default Vue.extend({
  components: {
    Sidebar,
  },
  data() {
    return {
      ac: null as AbortController | null,
      rows: [] as KeywordDedupListRow[],
      groups: {} as Record<string, KeywordDedupGroup>,
      filter: {
        search: '',
        market: '',
      },
      pagination: new Pagination({
        perPage: 50,
      }),
      filterDebounceId: null as number | null,
      openingGroupedKeywordKey: '',
      showLoading: false,
      tableError: '',
    };
  },
  watch: {
    '$route.query': {
      async handler(query: Record<string, any>) {
        this.loadStateFromQuery(query);
        this.syncQueryWithState(true);
        await this.getKeywords();
      },
      immediate: true,
    },
  },
  mounted() {
    document.body.classList.add(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  destroyed() {
    document.body.classList.remove(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
    this.ac?.abort();
    if (this.filterDebounceId !== null) {
      window.clearTimeout(this.filterDebounceId);
    }
  },
  methods: {
    async getKeywords() {
      try {
        this.showLoading = true;
        this.tableError = '';
        this.ac?.abort();
        this.ac = new AbortController();
        const {rows, count, groups} = await api.execute(
          'get',
          `/keyword-dedup/keywords?${this.getSearchParams().toString()}`,
          null,
          null,
          this.ac.signal,
        ) as KeywordDedupListResponse;
        this.rows = rows;
        this.groups = groups ?? {};
        this.pagination.total = count;
      } catch (e) {
        console.error(e);
        if (!axios.isCancel(e)) {
          this.rows = [];
          this.groups = {};
          this.pagination.total = 0;
          this.tableError = getErrorMessage(e);
          toasted.showErrorMessage(this.tableError);
        }
      } finally {
        this.showLoading = false;
      }
    },
    loadStateFromQuery(query: Record<string, any>) {
      this.filter.search = getQueryParamValue(query, 'search').trim();
      this.filter.market = normalizeMarket(getQueryParamValue(query, 'market'));
      this.pagination.loadFromQuery(query);
    },
    buildQueryFromState() {
      const query: Record<string, string> = {
        page: String(this.pagination.page),
        perPage: String(this.pagination.perPage),
      };
      if (this.filter.search.trim()) {
        query.search = this.filter.search.trim();
      }
      const market = normalizeMarket(this.filter.market);
      if (isValidMarket(market)) {
        query.market = market;
      }
      return query;
    },
    buildQueryFromRoute(query: Record<string, any>) {
      const normalizedQuery: Record<string, string> = {
        page: String(parseInt(getQueryParamValue(query, 'page'), 10) || 1),
        perPage: String(parseInt(getQueryParamValue(query, 'perPage'), 10) || 50),
      };
      const search = getQueryParamValue(query, 'search').trim();
      if (search) normalizedQuery.search = search;
      const market = getQueryParamValue(query, 'market').trim();
      if (market) normalizedQuery.market = market;
      return normalizedQuery;
    },
    serializeQuery(query: Record<string, string>) {
      const searchParams = new URLSearchParams();
      for (const key of Object.keys(query).sort()) {
        searchParams.append(key, query[key]);
      }
      return searchParams.toString();
    },
    syncQueryWithState(replace: boolean = false) {
      const targetQuery = this.buildQueryFromState();
      const currentQuery = this.buildQueryFromRoute(this.$route.query);
      if (this.serializeQuery(targetQuery) === this.serializeQuery(currentQuery)) return;
      this.updateQueryParams(targetQuery, replace);
    },
    updateQueryParams(query: Record<string, string>, replace: boolean = false) {
      if (replace) {
        this.$router.replace({query}).catch(noop);
      } else {
        this.$router.push({query}).catch(noop);
      }
    },
    getSearchParams() {
      const searchParams = new URLSearchParams();
      searchParams.append('page', String(this.pagination.page));
      searchParams.append('perPage', String(this.pagination.perPage));
      searchParams.append('column', 'keyword');
      searchParams.append('direction', 'asc');
      searchParams.append('includeGroupedKeywords', 'true');
      if (this.filter.search.trim()) {
        searchParams.append('search', this.filter.search.trim());
      }
      const market = normalizeMarket(this.filter.market);
      if (isValidMarket(market)) {
        searchParams.append('market', market);
      }
      return searchParams;
    },
    getRowGroup(row: KeywordDedupListRow) {
      if (!row.group_key || row.grouped_keyword_count === 0) return null;
      const group = this.groups[row.group_key];
      if (!group || !Array.isArray(group.keywords) || group.keywords.length === 0) return null;
      return group;
    },
    hasGroupedKeywords(row: KeywordDedupListRow) {
      return this.getRowGroup(row) !== null;
    },
    getGroupedKeywords(row: KeywordDedupListRow) {
      return this.getRowGroup(row)?.keywords ?? [];
    },
    getGroupedKeywordCountLabel(row: KeywordDedupListRow) {
      const count = row.grouped_keyword_count;
      return `${count} keyword${count === 1 ? '' : 's'}`;
    },
    getGroupedKeywordLookupKey(row: KeywordDedupListRow, keyword: string) {
      return `${row.id}:${row.group_key ?? row.cluster_id ?? 'none'}:${keyword}`;
    },
    isGroupedKeywordOpening(row: KeywordDedupListRow, keyword: string) {
      return this.openingGroupedKeywordKey === this.getGroupedKeywordLookupKey(row, keyword);
    },
    async findGroupedKeywordDetailId(row: KeywordDedupListRow, keyword: string) {
      if (normalizeKeywordMatchValue(row.keyword) === normalizeKeywordMatchValue(keyword)) {
        return row.id;
      }

      const searchParams = new URLSearchParams();
      searchParams.append('page', '1');
      searchParams.append('perPage', '500');
      searchParams.append('column', 'keyword');
      searchParams.append('direction', 'asc');
      searchParams.append('search', keyword.trim());
      const market = normalizeMarket(row.market);
      if (isValidMarket(market)) {
        searchParams.append('market', market);
      }

      const {rows} = await api.execute(
        'get',
        `/keyword-dedup/keywords?${searchParams.toString()}`,
      ) as KeywordDedupListResponse;

      const normalizedKeyword = normalizeKeywordMatchValue(keyword);
      const normalizedRecommendedKeyword = normalizeKeywordMatchValue(row.recommended_campaign_keyword);

      const exactMatches = rows.filter(candidate => (
        normalizeKeywordMatchValue(candidate.keyword) === normalizedKeyword &&
        normalizeMarket(candidate.market) === market
      ));

      const clusterMatch = row.cluster_id === null
        ? null
        : exactMatches.find(candidate => candidate.cluster_id === row.cluster_id);
      if (clusterMatch) return clusterMatch.id;

      const canonicalMatch = exactMatches.find(candidate => (
        normalizeKeywordMatchValue(candidate.recommended_campaign_keyword) === normalizedRecommendedKeyword
      ));
      if (canonicalMatch) return canonicalMatch.id;

      return exactMatches[0]?.id ?? null;
    },
    async openGroupedKeywordDetail(row: KeywordDedupListRow, keyword: string) {
      const lookupKey = this.getGroupedKeywordLookupKey(row, keyword);
      this.openingGroupedKeywordKey = lookupKey;

      try {
        const id = await this.findGroupedKeywordDetailId(row, keyword);
        if (id === null) {
          toasted.showErrorMessage(`Unable to open "${keyword}" because its detail row was not found.`);
          return;
        }

        await this.$router.push({
          name: 'KeywordDedupDetail',
          params: {id: String(id)},
          query: this.$route.query,
        }).catch(noop);
      } catch (e) {
        console.error(e);
        toasted.showErrorMessage(getErrorMessage(e));
      } finally {
        if (this.openingGroupedKeywordKey === lookupKey) {
          this.openingGroupedKeywordKey = '';
        }
      }
    },
    debounceFilterSync() {
      if (this.filterDebounceId !== null) {
        window.clearTimeout(this.filterDebounceId);
      }
      this.filterDebounceId = window.setTimeout(() => {
        this.syncQueryWithState();
        this.filterDebounceId = null;
      }, 500);
    },
    onSearchInput() {
      this.pagination.page = 1;
      this.debounceFilterSync();
    },
    onMarketInput(event: Event) {
      const input = event.target as HTMLInputElement;
      const market = normalizeMarket(input.value);
      input.value = market;
      this.filter.market = market;
      this.pagination.page = 1;
      this.debounceFilterSync();
    },
    onResetFilters() {
      if (this.filterDebounceId !== null) {
        window.clearTimeout(this.filterDebounceId);
        this.filterDebounceId = null;
      }
      this.filter.search = '';
      this.filter.market = '';
      this.pagination.page = 1;
      this.syncQueryWithState();
    },
    updatePage(page: number | null) {
      if (!page) return;
      this.pagination.page = page;
      this.syncQueryWithState();
    },
    getMarketFlag(market: string) {
      const normalized = String(market ?? '').trim().toUpperCase();
      const flagCode = normalized === 'UK' ? 'GB' : normalized;
      if (!/^[A-Z]{2}$/.test(flagCode)) return '';
      const codePoints = Array.from(flagCode).map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    },
  },
});
</script>
